/**
 * Shared user-type primitives.
 *
 * Single source of truth for the platform's user-type union. Imported by:
 *   - Backend auth middleware/service/controller/schemas (Zod validation
 *     uses the const tuple as its enum source)
 *   - Backend admin module (AdminUserType is now an alias)
 *   - Frontend auth store / service / route guards / analytics
 *
 * Adding a new role (e.g. for V2 multi-agent agencies, surveyors, etc.)
 * requires a single edit here. Without this, the union has historically
 * been redeclared at 8+ sites and drifted (PR #107 review found one site
 * — AdminUserType — that was missed when 'realtor' was added).
 *
 * The const tuple `USER_TYPES` is the type-source for `UserType` AND a
 * runtime tuple Zod and other validators can consume directly.
 */

export const USER_TYPES = [
	"homeowner",
	"designer",
	"contractor",
	"realtor",
] as const;

export type UserType = (typeof USER_TYPES)[number];

export type UserTypeOrNull = UserType | null;

/**
 * Roles that can participate in a Project (homeowner + service providers
 * they hire). Realtors do not participate in projects -- they have no
 * relationship to the Project entity. BoQ predicates, project access
 * checks, and other project-scoped permission code should accept
 * `ProjectParticipantRole`, not `UserType`.
 */
export const PROJECT_PARTICIPANT_ROLES = [
	"homeowner",
	"designer",
	"contractor",
] as const satisfies readonly UserType[];

export type ProjectParticipantRole = (typeof PROJECT_PARTICIPANT_ROLES)[number];

/**
 * Narrow a `UserType` (or null/undefined) to a `ProjectParticipantRole`.
 * Returns null for any non-participant role. Use at the boundary where a
 * UserType-typed value enters project-scoped permission code.
 */
export function toProjectParticipantRole(
	userType: UserType | null | undefined,
): ProjectParticipantRole | null {
	if (userType == null) return null;
	return (PROJECT_PARTICIPANT_ROLES as readonly string[]).includes(userType)
		? (userType as ProjectParticipantRole)
		: null;
}

/**
 * Roles that explicitly do NOT participate in projects. Listed separately
 * so the next person who adds a `UserType` is forced to think about which
 * bucket it belongs in (the `_AllRolesCovered` check below fails until
 * they do).
 */
const NON_PARTICIPANT_ROLES = [
	"realtor",
] as const satisfies readonly UserType[];
type NonParticipantRole = (typeof NON_PARTICIPANT_ROLES)[number];

/**
 * Compile-time exhaustiveness gate: the union of participant + non-
 * participant roles must equal `UserType`. If a new role is added to
 * `USER_TYPES` without classifying it, TypeScript fails this assignment
 * because `UserType` will be wider than `_AllRolesCovered`.
 */
type _AllRolesCovered = ProjectParticipantRole | NonParticipantRole;
const _exhaustivenessCheck = (role: UserType): _AllRolesCovered => role;
void _exhaustivenessCheck;
